<template>
  <div>
    <div
      class="mx-auto flex w-full max-w-content-container flex-col justify-center gap-y-7 px-small pb-20 pt-8 md:items-start xl:pb-36 xl:pl-16 xl:pr-0 xl:pt-10"
    >
      <Breadcrumb :menu="breadcrumbMenu" />
      <div class="flex w-full flex-col gap-y-6">
        <SectionTitle :title="title" authorized :icon="icon" />
        <Hero
          v-if="!isViewMoreActive"
          :loading="loading"
          :market="slug"
          :articles="articles"
          :videos="videos"
          :events="events"
          @play-video="playVideo"
        />
        <TheTabsBySlug :tabs="tabs" />
        <div class="lg:min-h-[32rem]">
          <slot
            :slug="slug"
            :categories-articles="categoriesArticles"
            :categories-videos="categoriesArticles"
            :play-video="playVideo"
          />
        </div>
      </div>
    </div>

    <VideoModal
      v-if="vimeoVideo && $modal.getModal('videoIntro')"
      :vimeo-source="vimeoSource"
      :video-ratio="videoRatio"
      :is-preview="isPreviewVideo"
    />

    <PdfViewerModal :url="pdfUrl" @close="closeViewer" />
  </div>
</template>

<script lang="ts" setup>
import type { TMarketServiceConfig } from '~/src/types/markets'
import { logError } from '~/utils/log-error'
import { useBreadcrumbStore } from '~/stores/breadcrumb'
import { getMarketInsightsTabs } from '~/src/utils/tabs'
import { getServices } from '~/constants/services'
import { useGetVimeoData } from '~/composables/useGetVimeoData'
import useAuthentication from '~/composables/useAuthentication'
import { MARKET_TYPE } from '~/src/constants/markets'
import { useReports } from '~/composables/useReports'
import { reformat } from '~/utils/date'
import { useModalStore } from '~/stores/modal'

import Breadcrumb from '~/components/shared/navigation/Breadcrumb.vue'
import Hero from '~/components/market-insights/Hero.vue'
import SectionTitle from '~/components/price-assessments/SectionTitle.vue'
import TheTabsBySlug from '~/components/common/TheTabsBySlug.vue'
import VideoModal from '~/components/video/VideoModal.vue'
import PdfViewerModal from '~/components/critical-insights/PdfViewerModal.vue'

const props = defineProps<{
  market: TMarketServiceConfig
}>()

const { $graphqlClient, $modal, $rudderstack } = useNuxtApp()
const breadcrumbStore = useBreadcrumbStore()
const { getReportAndModelData } = useReports()
const { getVimeoData, handlerPlay, vimeoSource, videoRatio, vimeoVideo } =
  useGetVimeoData()
const { items: breadcrumbMenu } = storeToRefs(breadcrumbStore)
const initialValues = getServices([props.market, 'source'])
const modalStore = useModalStore()
const { modalParams } = storeToRefs(modalStore)

const isViewMoreActive = useState<boolean>('isViewMoreActive')

const loading = ref(true)
const articles = ref([])
const videos = ref([])
const events = ref([])
const isPreviewVideo = ref(false)

const pdfUrl = computed(() => modalParams.value?.pdfUrl)
const icon = computed(() => props.market.iconColor)
const title = computed(() => `${props.market.name} Market Insights`)
const slug = computed(() => props.market.slug)
const categoriesArticles = computed(
  () => props.market.categoriesArticles ?? null,
)

const tabs = computed(() =>
  getMarketInsightsTabs({
    market: props.market.alternativeSlug ?? props.market.slug,
  }),
)

const closeViewer = () => {
  modalStore.setModalParams({ pdfUrl: '' })
}

const playVideo = async (vimeoData: any) => {
  const vimeoId = vimeoData.vimeoId || vimeoData.previewVimeoId
  isPreviewVideo.value = !vimeoData.vimeoId && !!vimeoData.previewVimeoId
  $modal.setInitialFields(initialValues)

  if (!vimeoId) {
    $rudderstack.trackContentGatedInteraction()
    $modal.open('contact')
    return
  }

  await getVimeoData(vimeoId)
  handlerPlay()
}

const getCriticalInsights = async () => {
  const reports = await getReportAndModelData(
    slug.value,
    [MARKET_TYPE.CRITICAL_INSIGHTS],
    true,
    1,
  )
  return reports.map((report) => ({
    ...report,
    publishedDate: reformat(report.date, 'dd MMM yyyy'),
  }))
}

const onInit = async () => {
  try {
    const [criticalInsights, marketInsights] = await Promise.all([
      getCriticalInsights(),
      $graphqlClient.getMarketInsightsArticles({
        market: slug.value,
        categories: categoriesArticles.value,
      }),
    ])

    articles.value = marketInsights.articles
      .toSpliced(2, 0, ...criticalInsights)
      .slice(0, 5)
    videos.value = marketInsights.videos
    events.value = marketInsights.events
  } catch (error) {
    logError(error)
  } finally {
    loading.value = false
  }
}

onMounted(onInit)
useAuthentication(onInit)
</script>
